import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, Typography, Box, Chip, IconButton, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import FlipIcon from '@mui/icons-material/Flip';
import CancelIcon from '@mui/icons-material/Cancel';
import { Flashcard as FlashcardType } from '../types';
import { MarkdownContent } from './MarkdownContent';

const StyledCard = styled(Card)(({ theme }) => ({
  minHeight: 320,
  cursor: 'pointer',
  position: 'relative',
  borderRadius: 20,
  overflow: 'hidden',
  border: '1px solid rgba(15, 23, 42, 0.12)',
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
  [theme.breakpoints.down('sm')]: {
    minHeight: 260,
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 28px 70px rgba(15, 23, 42, 0.22)',
  },
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
}));

const FlipContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: 320,
  [theme.breakpoints.down('sm')]: {
    minHeight: 260,
  },
}));

const CardFace = styled(motion.div)({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  borderRadius: '20px',
  display: 'flex',
  flexDirection: 'column',
});

const CardFront = styled(CardFace)({
  background: 'linear-gradient(180deg, #ffffff 0%, #eaf1ff 100%)',
  color: '#0f172a',
});

const CardBack = styled(CardFace)({
  background: 'linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)',
  color: '#0f172a',
});

const DifficultyChip = styled(Chip)<{ difficulty: string }>(({ difficulty }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: difficulty === 'easy' ? '#14b86a' :
                   difficulty === 'medium' ? '#ffb020' : '#ef4444',
  color: 'white',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
}));

const QualityButton = styled(Button)({
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 600,
  minWidth: 78,
  padding: '6px 12px',
  border: '1px solid rgba(15, 23, 42, 0.2)',
  color: '#0f172a',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  '&.again': {
    borderColor: '#ef4444',
    color: '#991b1b',
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
  },
  '&.hard': {
    borderColor: '#f97316',
    color: '#9a3412',
    backgroundColor: 'rgba(249, 115, 22, 0.12)',
  },
  '&.good': {
    borderColor: '#10b981',
    color: '#047857',
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
  },
  '&.easy': {
    borderColor: '#3b82f6',
    color: '#1d4ed8',
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
  },
});

interface FlashcardProps {
  flashcard: FlashcardType;
  onFlip?: () => void;
  onAnswer?: (quality: number) => void;
  showAnswer?: boolean;
  studyMode?: boolean;
}

export const Flashcard: React.FC<FlashcardProps> = ({
  flashcard,
  onFlip,
  onAnswer,
  showAnswer = false,
  studyMode = false
}) => {
  const [isFlipped, setIsFlipped] = useState(showAnswer);

  const buildCardSides = (card: FlashcardType) => {
    const rawAnswer = card.answer || '';

    const extractSection = (label: string) => {
      const pattern = new RegExp(`(?:^|\\n)${label}:\\s*([\\s\\S]*?)(?=\\n(?:Content|Images|Speaker Notes):|$)`, 'i');
      const match = rawAnswer.match(pattern);
      return match ? match[1].trim() : '';
    };

    const content = extractSection('Content');
    const images = extractSection('Images');
    const speakerNotes = extractSection('Speaker Notes');
    const frontParts = [content, images].filter(Boolean).join('\n\n');

    const stripHeaders = (value: string) => value
      .replace(/^\s*(Content|Images|Speaker Notes):\s*/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const scrubbed = rawAnswer.replace(/Speaker Notes:\s*[\s\S]*/i, '').trim();

    const front = frontParts || stripHeaders(scrubbed) || card.question;
    const back = speakerNotes || card.answer;

    return { front, back };
  };

  const { front, back } = buildCardSides(flashcard);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    onFlip?.();
  };

  const handleAnswer = (quality: number) => {
    onAnswer?.(quality);
    setIsFlipped(false);
  };

  const handleSwipe = (_event: any, info: { offset: { x: number } }) => {
    if (!studyMode || !onAnswer) return;
    if (Math.abs(info.offset.x) < 120) return;

    const quality = info.offset.x > 0 ? 3 : 1;
    handleAnswer(quality);
  };

  return (
    <motion.div
      onTap={handleFlip}
      drag={studyMode ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.12}
      onDragEnd={handleSwipe}
      style={{ width: '100%' }}
    >
      <StyledCard
        sx={studyMode ? {
          height: { xs: '72vh', sm: '68vh', md: 560 },
          minHeight: { xs: '72vh', sm: '68vh', md: 560 }
        } : undefined}
      >
        <FlipContainer>
        {/* Front of card */}
        <CardFront
          initial={false}
          animate={{ opacity: isFlipped ? 0 : 1, scale: isFlipped ? 0.98 : 1 }}
          transition={{ duration: 0.2 }}
          style={{ pointerEvents: isFlipped ? 'none' : 'auto' }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 3 } }}>
            <DifficultyChip
              label={flashcard.difficulty}
              difficulty={flashcard.difficulty}
              size="small"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" component="div" sx={{ flexGrow: 1, opacity: 0.8 }}>
                {flashcard.topic}
              </Typography>
              {!studyMode && (
                <IconButton onClick={(e) => { e.stopPropagation(); handleFlip(); }}>
                  <FlipIcon sx={{ color: 'inherit' }} />
                </IconButton>
              )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
              <MarkdownContent content={front} />
            </Box>

          </CardContent>
        </CardFront>

        {/* Back of card */}
        <CardBack
          initial={false}
          animate={{ opacity: isFlipped ? 1 : 0, scale: isFlipped ? 1 : 0.98 }}
          transition={{ duration: 0.2 }}
          style={{ pointerEvents: isFlipped ? 'auto' : 'none' }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 3 } }}>
            <DifficultyChip
              label={flashcard.difficulty}
              difficulty={flashcard.difficulty}
              size="small"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" component="div" sx={{ flexGrow: 1, opacity: 0.8 }}>
                Notes
              </Typography>
              {!studyMode && (
                <IconButton onClick={(e) => { e.stopPropagation(); handleFlip(); }}>
                  <FlipIcon sx={{ color: 'inherit' }} />
                </IconButton>
              )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
              <MarkdownContent content={back} />
            </Box>

            {studyMode && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center' }}>
                  How well did you know this?
                </Typography>
                <QualityButton
                  className="again"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleAnswer(1); }}
                  startIcon={<CancelIcon />}
                >
                  Again
                </QualityButton>
                <QualityButton
                  className="hard"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleAnswer(2); }}
                >
                  Hard
                </QualityButton>
                <QualityButton
                  className="good"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleAnswer(3); }}
                >
                  Good
                </QualityButton>
                <QualityButton
                  className="easy"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); handleAnswer(4); }}
                >
                  Easy
                </QualityButton>
              </Box>
            )}
          </CardContent>
        </CardBack>
        </FlipContainer>
      </StyledCard>
    </motion.div>
  );
};
